import { ORG_PATH_PREFIX } from '@/lib/routes';

/**
 * First path segments (relative to the org base, `/orgs/:slug/`) that belong
 * to the Configure surface. These are the configuration subtrees the
 * `ConfigureShell` takeover wraps: the `/configure/*` tree, legacy host
 * drills still redirecting into it (`org/*`, `security/*`), and the
 * plugin-contributed pages that still answer at their pre-migration paths
 * long enough for redirects to fire.
 *
 * Phase 1 of the chat-first shell rework intentionally classifies by path so
 * URLs do not move; a manifest-driven `surface: 'configure'` flag can replace
 * this in a later phase.
 */
const ORG_CONFIGURE_FIRST_SEGMENTS: ReadonlySet<string> = new Set([
  'configure',
  'org',
  'security',
  'agent-types',
  'agent-personas',
  'blueprints',
  'mcp-servers',
  'ai-gateways',
  'tailscale',
  'network-policies',
]);

/**
 * Returns true when the org-relative path is under `/configure/connect/...`,
 * which belongs to the Connect takeover surface, not Configure. Called by
 * {@link isConfigurePath} to carve out the Connect subtree.
 */
function isOrgConnectSubtree(segments: string[]): boolean {
  // /orgs/:slug/configure/connect/...
  return segments[2] === 'configure' && segments[3] === 'connect';
}

/**
 * Org-scoped legacy aliases that redirect into a Configure path (see the
 * redirect routes in `App.tsx`, e.g. `/pylon` ->
 * `configure/connect/integrations/pylon`). They must classify as Configure
 * even though they never render: the app shell's return-path tracker would
 * otherwise record them as work locations, and Back / `esc` would bounce
 * straight back into Configure.
 */
const ORG_CONFIGURE_ALIAS_FIRST_SEGMENTS: ReadonlySet<string> = new Set([
  'integrations',
  'pylon',
  'linear',
  'slack',
  'sentry',
  'pr-reviews',
]);

/**
 * Root-level legacy aliases that redirect into Configure: `/credentials` and
 * `/me/secrets` forward to the org Security pages, and the slug-less
 * pre-`/orgs` config paths (`/org/...`, `/agent-types`, ...) forward into
 * `/orgs/:activeSlug/<path>`. Same poisoning concern as the org aliases.
 *
 * Personal `/settings` and `/profile` deliberately do NOT classify: they
 * render on the plain app shell now that Configure is org-only.
 */
const ROOT_CONFIGURE_ALIAS_FIRST_SEGMENTS: ReadonlySet<string> = new Set([
  'personas',
  'credentials',
  'me',
  ...ORG_CONFIGURE_FIRST_SEGMENTS,
]);

/**
 * True when `pathname` renders inside the Configure takeover (or is a legacy
 * alias that immediately redirects there). The app shell uses this to drop
 * the main sidebar (the takeover brings its own nav) and to skip the path
 * when remembering the last work location.
 *
 * Only canonical URL shapes are considered: `/orgs/:slug/...` and the
 * root-level legacy aliases above. Slug-ful pre-`/orgs` shapes
 * (`/acme/org/members`) redirect through `LegacyOrgPathRedirect` before they
 * matter here.
 */
export function isConfigurePath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0];
  if (!first) return false;
  if (first === ORG_PATH_PREFIX) {
    const orgFirst = segments[2];
    if (orgFirst === undefined) return false;
    // Connect surface lives at /configure/connect — not Configure.
    if (isOrgConnectSubtree(segments)) return false;
    if (ORG_CONFIGURE_FIRST_SEGMENTS.has(orgFirst)) return true;
    if (ORG_CONFIGURE_ALIAS_FIRST_SEGMENTS.has(orgFirst)) return true;
    // `plugins/:pluginName` and `org/plugins/:name` redirect into Connect.
    return (
      (orgFirst === 'plugins' && segments[3] !== undefined) ||
      (orgFirst === 'org' && segments[3] === 'plugins' && segments[4] !== undefined)
    );
  }
  if (ROOT_CONFIGURE_ALIAS_FIRST_SEGMENTS.has(first)) return true;
  // `/admin/agent-models` is a legacy alias into org Configure; the rest of
  // `/admin` is the instance-admin surface, not Configure.
  return first === 'admin' && segments[1] === 'agent-models';
}

/**
 * Classifies an org-scoped plugin route path (root-relative, e.g.
 * `/configure/usage/model-costs/*` or `/configure/connect/mcp`) as belonging
 * to the Configure surface. `App.tsx` uses this to mount config plugin routes
 * inside the shell; everything else is claimed by another takeover
 * (Automations, Labs, Account) or falls through to the plain app shell.
 *
 * Legacy first segments (`mcp-servers`, `org`, …) stay classified so any
 * still-mounted redirect routes keep the Configure frame for one hop.
 */
export function isConfigurePluginRoutePath(path: string): boolean {
  const first = path.split('/').filter(Boolean)[0];
  return first !== undefined && ORG_CONFIGURE_FIRST_SEGMENTS.has(first);
}
